import { PageHead } from "@/components/PageHead";

const Code = ({ children }: { children: string }) => (
  <pre className="mono" style={{
    background: "var(--well)", border: "1px solid var(--line)", borderRadius: 10,
    padding: "12px 14px", fontSize: 12, lineHeight: 1.6, overflowX: "auto",
    whiteSpace: "pre", margin: "8px 0 0",
  }}>{children}</pre>
);

export function ApiOverviewPage() {
  const base = window.location.origin;
  return (
    <div className="mpage on">
      <PageHead eyebrow="API · Overview" title="Integrating with Orbit"
        desc="Everything Orbit's own console does goes through this same HTTP API — there is no privileged side channel. The Reference tab renders the live schema of this exact deployment." />

      <div className="section-label">Base URL</div>
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <Code>{base}</Code>
        <p style={{ fontSize: 12.5, color: "var(--t-mute)", marginTop: 10 }}>
          HTTPS only. All request and response bodies are JSON unless an endpoint says otherwise
          (file uploads are multipart; file downloads are raw bytes).
        </p>
      </div>

      <div className="section-label">Authentication — two surfaces</div>
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 13, lineHeight: 1.7 }}>
          <b>1. Console API (everything under <span className="mono">/modules/*</span>)</b> — cookie
          session. Log in once, keep the cookie, call anything your account's permission gates allow.
          Server-to-server integrations use the same flow with a cookie jar:
        </p>
        <Code>{`# login (sets the session cookie)
curl -c jar.txt -X POST ${base}/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"login_id": "<user>", "password": "<password>"}'

# authenticated call
curl -b jar.txt ${base}/modules/drivers/stats`}</Code>
        <p style={{ fontSize: 13, lineHeight: 1.7, marginTop: 14 }}>
          <b>2. Companion API (<span className="mono">/companion/*</span>)</b> — bearer tokens for
          the driver mobile app: phone-number proof (Firebase/OTP) mints a session token, a
          hardware-bound key keeps it silent afterwards. The full contract both sides build against
          lives in <span className="mono">docs/architecture/DRIVER_APP.md</span>. (Endpoints land
          with the companion-auth build.)
        </p>
        <p style={{ fontSize: 12.5, color: "var(--t-mute)", marginTop: 10 }}>
          Access is permission-gated per module (a 403 carries the missing gate). API keys for
          third-party server integrations are a planned surface — until then, integrations use a
          dedicated Orbit account over the cookie flow.
        </p>
      </div>

      <div className="section-label">Conventions</div>
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <ul style={{ fontSize: 13, lineHeight: 1.9, paddingLeft: 18, margin: 0 }}>
          <li>Timestamps: ISO-8601 with timezone, UTC (<span className="mono">2026-08-19T17:12:09+00:00</span>).</li>
          <li>Phone numbers: E.164 (<span className="mono">+919876543210</span>); bare Indian 10-digit input is normalized to <span className="mono">+91</span>.</li>
          <li>Lists paginate with <span className="mono">limit</span> / <span className="mono">offset</span> and return <span className="mono">total</span>; responses that hit a cap say so (<span className="mono">truncated</span>).</li>
          <li>Errors: <span className="mono">{`{"detail": {"error_code": "...", "message": "..."}}`}</span> — code is stable and machine-readable, message is for humans.</li>
          <li>Mutations are POST/PUT and idempotent where the resource allows; conflicts return 409 with a reason, never silent overwrites.</li>
        </ul>
      </div>

      <div className="section-label">Machine-readable schema</div>
      <div className="panel panel-pad">
        <p style={{ fontSize: 13, lineHeight: 1.7 }}>
          The complete OpenAPI 3 description of this deployment — every path, parameter, and model —
          is served at:
        </p>
        <Code>{`${base}/openapi.json`}</Code>
        <p style={{ fontSize: 12.5, color: "var(--t-mute)", marginTop: 10 }}>
          Generate clients from it (openapi-generator, orval, oazapfts…) instead of hand-writing
          bindings — the Reference tab is rendered from this same document.
        </p>
      </div>
    </div>
  );
}
